import { isJson, objectStringToJson } from "../utils/json";

export default class InstanceMgr {

    // constructor
    constructor(args = {}) {
        
        // set properties
        this._instances = new Map();
        this._$parentNode = args.$parentNode || document.body;
    }

    // methods
    add (key, ...instances) {
        if (!this._instances.has(key)) this._instances.set(key, new Set());
        
        const list = this._instances.get(key);
        for (const instance of instances) {
            list.add(instance);
        }
    }

    init(key, selector, Class, params = {}) {

        // support for (key, Class, [params])
        if (typeof selector !== 'string') {
            params = Class || {}
            Class = selector;
            selector = null;
        }

        // lookup elements if selector is given
        if (selector) {
            const $els = this._$parentNode.querySelectorAll(selector);
            for (const $el of $els) {
                this.add(key, new Class(proccessedParams(selector, params, $el)));
            }
        }
        else {
            this.add(key, new Class(proccessedParams(selector, params)));
        }
    }

    get(key, asArray = false) {
        const result = (this._instances.get(key) || new Set());
        if (asArray) return Array.from(result);
        return result;
    }

    find(key, processor = null) {
        if (typeof key === 'string') {
            return this._instances.get(key, true).find(processor);
        }
        else {
            return this._instances.values().find(processor);
        }
    }

    filter(key, processor = null) {
        if (typeof key === 'string') {
            return this._instances.get(key, true).filter(processor);
        }
        else {
            return this._instances.values().filter(processor);
        }
    }

    has(key) {
        return this._instances.has(key);
    }

    run(keys, prop, ...args) {
        // make sure it's an array
        if (!Array.isArray(keys)) keys = [keys];

        // loop over given keys
        for (const key of keys) {

            // extract list
            const list = this._instances.get(key);

            if (list) {
                // loop over instances in list
                for (const instance of list.values()) {
                    if (typeof prop === 'string') {
                        applyValueToProp(instance, prop, args[prop]);
                    }
                    else if (typeof prop === 'object') {
                        // translate prop var to args for readability
                        args = prop;

                        for (prop of Object.keys(args)) {
                            applyValueToProp(instance, prop, args[prop]);
                        }
                    }
                }
            }
        }
    }


    /**
     * Destroy
     * value == null => clear instance map
     * value == true => clear instance map & destroy all instances along the way
     * value == string => clear all instances on given key, clear & remove the map
     * value == array => clear all instances on given keys, clear & remove the map
     */
    destroy(value = null) {
        if (value === null || value === true) {
            if (value === true) {
                // destory all registered instances (loop over each key and use recusion)
                for (const key of this._instances.keys()) {
                    this.destroy(key);
                }
            }

            // destroy the map
            this._instances.clear();
        }
        else {
            // make sure it is an array of strings
            if (typeof value === 'string') value = [value];

            // loop over all strings (keys) and destroy all instances on each key
            for (const key of value) {

                const instances = this._instances.get(key);

                // destroy each instance
                if (instances) {
                    for (const instance of instances.values()) {
                        instance.destroy();
                    }

                    // clear map
                    instances.clear();
                    this._instances.delete(key);
                }

            }
        }
    }

    // getters & setters
    get entries () {
        return this._instances.entries();
    }

    get instances () {
        return this._instances.values();
    }

    get keys() {
        return this._instances.keys();
    }
}

// utils
function proccessedParams(selector, params = {}, $el = null) {
    // extract parameters if selector ends in attribute (considering it may be usefull for init)
    const attributeRegex = /\[([^\]]+)\]$/; // Regular expression to match attribute selectors at the end
    const match = selector ? selector.match(attributeRegex) : false;
    const attributeName = match ? match[1] : null;

    if (typeof params === 'function') {
        if ($el) {
            if (attributeName) {
                return params({ 
                    $el,
                    params: processAttributeValue($el.getAttribute(attributeName))
                });
            }
            else {
                return params({ $el });
            }
        }
        else {
            return params({});
        }
    }

    // add $el to params unless it's not wanted (when dev set $el of params to null)
    if ($el && params.$el !== null) {
        params.$el = $el;
    }

    return params;
}

function applyValueToProp(instance, prop, args = undefined) {
    if (typeof instance[prop] === 'function') {
        if (Array.isArray(args)) {
            instance[prop](...args);
        } 
        else if (args) {
            instance[prop](args);
        }
        else {
            instance[prop]()
        }
    }
    else {
        instance[prop] = args
    }
}

function processAttributeValue(value) {
    if (isJson(value)) {
        return JSON.parse(objectStringToJson(value));
    }
    else if (value.indexOf('|')) {
        return value.split('|')
    }

    return value;
}

// events